import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { Box, Heading, Icon, Layout, Paragraph, Tag, TextButton, ToggleCard } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconCheckmarkCircleOutline,
  IconLightningFilled,
  IconLightningOutline,
} from '@zillow/constellation-icons';

import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

export type TourSchedulingBookingMethodBodyProps = {
  selectedBookingMethod?: TourConfirmationType;
  onSelectBookingMethod: (bookingMethod: TourConfirmationType | undefined) => void;
};

const TourSchedulingBookingMethodBody = ({
  selectedBookingMethod,
  onSelectBookingMethod,
}: TourSchedulingBookingMethodBodyProps) => {
  return (
    <div data-testid="TourSchedulingBookingMethodBody">
      <WizardFormSectionContainer>
        <WizardHeading level={1} textStyle="heading-sm" data-testid="TourSchedulingBookingMethodBody-Heading">
          Choose a way to book tours
        </WizardHeading>
        <WizardBody>
          <Layout.Root>
            <Layout.Content css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' }, padding: 'zero' }}>
              <Paragraph data-testid="TourSchedulingBookingMethodBody-Description">
                You can change your settings anytime.{' '}
                <strong>Skip this if you prefer to book tours on your own.</strong>
              </Paragraph>
            </Layout.Content>
            <Layout.Content css={{ gridColumnEnd: { base: 'span 0', md: 'span 4' }, padding: 'zero' }}></Layout.Content>
          </Layout.Root>
        </WizardBody>

        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'default' }}>
          <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' }, paddingLeft: 'zero' }}>
            <ToggleCard
              selected={selectedBookingMethod === TourConfirmationType.INSTANT}
              onClick={() =>
                onSelectBookingMethod(
                  selectedBookingMethod === TourConfirmationType.INSTANT ? undefined : TourConfirmationType.INSTANT,
                )
              }
              elevated={false}
              data-testid="TourSchedulingBookingMethodBody-BookingMethodCard-BookInstantly"
              tone={selectedBookingMethod === TourConfirmationType.INSTANT ? 'soft' : 'neutral'}
              css={{ height: '100%' }}
            >
              <Box css={{ margin: '200' }}>
                {selectedBookingMethod === TourConfirmationType.INSTANT ? (
                  <Icon size="lg" css={{ marginBottom: 'loose', color: 'blue.500' }}>
                    <IconLightningFilled />
                  </Icon>
                ) : (
                  <Icon size="lg" css={{ marginBottom: 'loose', color: 'black' }}>
                    <IconLightningOutline />
                  </Icon>
                )}
                <Box css={{ marginBottom: 'tighter' }}>
                  <Heading level={5}>Book tours instantly</Heading>
                </Box>
                <Tag tone="gray" size="sm" css={{ marginBottom: 'loose' }}>
                  MOST EFFICIENT
                </Tag>
                <Paragraph css={{ marginBottom: 'tighter' }}>
                  Renters directly book a time from the availability provided on your listing.
                </Paragraph>
              </Box>
            </ToggleCard>
          </Box>
          <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' }, paddingLeft: 'zero' }}>
            <ToggleCard
              selected={selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM}
              onClick={() =>
                onSelectBookingMethod(
                  selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM
                    ? undefined
                    : TourConfirmationType.LANDLORD_CONFIRM,
                )
              }
              elevated={false}
              data-testid="TourSchedulingBookingMethodBody-BookingMethodCard-ReviewAndConfirm"
              tone={selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM ? 'soft' : 'neutral'}
              css={{ height: '100%' }}
            >
              <Box css={{ margin: '200' }}>
                {selectedBookingMethod === TourConfirmationType.LANDLORD_CONFIRM ? (
                  <Icon size="lg" css={{ marginBottom: 'loose', color: 'blue.500' }}>
                    <IconCheckmarkCircleFilled />
                  </Icon>
                ) : (
                  <Icon size="lg" css={{ marginBottom: 'loose', color: 'black' }}>
                    <IconCheckmarkCircleOutline />
                  </Icon>
                )}
                <Box css={{ marginBottom: 'tighter' }}>
                  <Heading level={5}>Review and confirm</Heading>
                </Box>
                <Tag tone="gray" size="sm" css={{ marginBottom: 'loose' }}>
                  MORE CONTROL
                </Tag>
                <Paragraph>
                  Renters request a time from the availability on your listing, then you confirm or decline.
                </Paragraph>
              </Box>
            </ToggleCard>
          </Box>
        </Box>

        {selectedBookingMethod && (
          <TextButton
            data-testid="TourSchedulingBookingMethodBody-ClearButton"
            css={{ marginTop: 'loose' }}
            onClick={() => onSelectBookingMethod(undefined)}
          >
            Clear
          </TextButton>
        )}
      </WizardFormSectionContainer>
    </div>
  );
};

export default TourSchedulingBookingMethodBody;
